import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, ExternalLink, Link2, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/noleggio";

const SHEET_URL = "https://docs.google.com/spreadsheets/d/1rA9H1lmGsq_LoDxJF95TCEp994Icw3AD_naY0LtkK0U/edit";

/** Guides the owner through linking the Apps Script that writes website bookings into the Google Sheet. */
export default function AdminSheetLink({ onClose }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["sheet-script"], queryFn: () => api("/admin/sheet/script") });
  const [url, setUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(q.data.script);
    setCopied(true);
    toast.success("Codice copiato");
  };
  const save = async () => {
    setBusy(true);
    try {
      await api("/admin/sheet/script-url", { method: "PUT", body: { url: url.trim() } });
      toast.success("Foglio collegato: le nuove prenotazioni verranno scritte nel foglio");
      qc.invalidateQueries({ queryKey: ["sheet-status"] });
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  const step = (n, title, children) => (
    <div className="flex gap-3">
      <span className="w-7 h-7 rounded-full bg-[var(--brand-primary)] text-white text-sm font-extrabold flex items-center justify-center shrink-0">{n}</span>
      <div className="flex-1 min-w-0 flex flex-col gap-2"><p className="font-bold text-sm">{title}</p>{children}</div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center" onClick={onClose}>
      <div data-testid="sheet-link-dialog" onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-xl max-h-[92vh] overflow-y-auto rounded-t-3xl md:rounded-3xl p-6 flex flex-col gap-5 mn-rise">
        <div className="flex items-center"><h2 className="flex-1 text-xl font-extrabold">Collega il foglio Google</h2>
          <button data-testid="sheet-link-close" onClick={onClose} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-[var(--surface-2)]"><X /></button></div>
        <p className="text-sm text-[var(--text-2)]">Serve una volta sola: così ogni prenotazione fatta dal sito viene scritta nel "Registro storico noleggi".</p>
        {step(1, "Apri il foglio e vai su Estensioni → Apps Script", (
          <a href={SHEET_URL} target="_blank" rel="noopener noreferrer" data-testid="sheet-link-open-sheet" className="text-sm font-bold text-[var(--brand-primary)] flex items-center gap-1">Apri il foglio <ExternalLink size={14} /></a>
        ))}
        {step(2, "Cancella il codice che c'è e incolla questo", (
          <button data-testid="sheet-link-copy" disabled={!q.data} onClick={copy} className="mn-btn mn-btn-ghost self-start !min-h-[42px] text-sm">
            {q.isLoading ? <Loader2 size={16} className="animate-spin" /> : copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copiato" : "Copia il codice"}
          </button>
        ))}
        {step(3, "Premi Esegui il deployment → Nuovo deployment", (
          <p className="text-sm text-[var(--text-2)]">Tipo: <b>Applicazione web</b> · Esegui come: <b>Me</b> · Chi ha accesso: <b>Chiunque</b>. Autorizza con il tuo account e copia l'URL che finisce con <b>/exec</b>.</p>
        ))}
        {step(4, "Incolla qui l'URL e premi Collega", (
          <>
            <input data-testid="sheet-link-url" className="mn-input" placeholder="https://script.google.com/macros/s/.../exec" value={url} onChange={(e) => setUrl(e.target.value)} />
            <button data-testid="sheet-link-save" className="mn-btn" disabled={busy || !url.trim()} onClick={save}>{busy ? <Loader2 size={18} className="animate-spin" /> : <Link2 size={18} />} Collega</button>
          </>
        ))}
        {q.data?.url && <p className="text-xs text-[var(--muted)] break-all">Collegato ora a: {q.data.url}</p>}
      </div>
    </div>
  );
}
